"use client";

import { useState, useEffect, useCallback } from "react";
import { Notify } from "notiflix/build/notiflix-notify-aio";
import axios from "axios";
import { useTranslation } from "react-i18next";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Getorder.css";
import "./modal.css";

const GetOrder = () => {
    const { t } = useTranslation();
    const [orders, setOrders]           = useState([]);
    const [searchTerm, setSearchTerm]   = useState("");
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showModal, setShowModal]     = useState(false);

    const [page, setPage]               = useState(0);
    const size = 10;
    const [totalPages, setTotalPages]   = useState(0);

    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

    const fetchOrders = useCallback(async () => {
        try {
            const token = sessionStorage.getItem("accessToken");
            if (!token) {
                Notify.failure(t('orders.errors.tokenNotFound'));
                return;
            }

            const { data } = await axios.get(
                `${apiBaseUrl}/manager/orders/all`,
                {
                    params: { page, size },
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            setOrders(Array.isArray(data.content) ? data.content : []);
            setTotalPages(data.totalPages ?? 0);
        } catch (err) {
            console.error("Ошибка при получении заказов:", err);
            Notify.failure(t('orders.errors.fetchFailed'));
        }
    }, [apiBaseUrl, page, t]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    const filteredOrders = orders.filter((order) => {
        let txt = "";
        Object.entries(order).forEach(([k, v]) => {
            if (k !== "items") txt += String(v).toLowerCase() + " ";
        });
        if (order.items) {
            order.items.forEach((i) => {
                txt += (i.name||"").toLowerCase() + " ";
                txt += (i.quantity||"") + " ";
            });
        }
        return txt.includes(searchTerm.toLowerCase());
    });

    const openModal  = (order) => { setSelectedOrder(order); setShowModal(true); };
    const closeModal = () => { setSelectedOrder(null); setShowModal(false); };

    const renderModal = () => {
        if (!showModal || !selectedOrder) return null;
        return (
            <div className="modal-overlay" onClick={closeModal}>
                <div className="modal-content" onClick={e => e.stopPropagation()}>
                    <div className="modal-header">
                        <h5 className="modal-title">{t('orders.modal.title')} #{selectedOrder.orderId}</h5>
                        <button className="close-button" onClick={closeModal}>&times;</button>
                    </div>
                    <div className="modal-body">
                        <p><strong>{t('orders.modal.orderDate')}:</strong> {new Date(selectedOrder.orderDate).toLocaleString()}</p>
                        <p><strong>{t('orders.modal.status')}:</strong> {selectedOrder.status}</p>
                        <p><strong>{t('orders.modal.totalPrice')}:</strong> {selectedOrder.totalPrice}</p>
                        <p><strong>{t('orders.modal.firstname')}:</strong> {selectedOrder.firstname}</p>
                        <p><strong>{t('orders.modal.city')}:</strong> {selectedOrder.city}</p>
                        <p><strong>{t('orders.modal.country')}:</strong> {selectedOrder.country}</p>
                        <h5>{t('orders.modal.items')}:</h5>
                        {selectedOrder.items?.length > 0 ? (
                            <ul>
                                {selectedOrder.items.map((item, i) => (
                                    <li key={i}>{item.name} × {item.quantity}</li>
                                ))}
                            </ul>
                        ) : <p>{t('orders.modal.noItems')}</p>}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="mt-4">
            {/* Поиск */}
            <div className="row mb-3">
                <div className="col">
                    <input
                        type="text"
                        className="form-control"
                        placeholder={t('orders.searchPlaceholder')}
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Таблица */}
            {filteredOrders.length > 0 ? (
                <div className="table-responsive">
                    <table className="table table-hover">
                        <thead className="thead-light">
                        <tr>
                            <th>{t('orders.table.orderId')}</th>
                            <th>{t('orders.table.orderDate')}</th>
                            <th>{t('orders.table.status')}</th>
                            <th>{t('orders.table.totalPrice')}</th>
                            <th>{t('orders.table.firstname')}</th>
                            <th>{t('orders.table.city')}</th>
                            <th>{t('orders.table.country')}</th>
                            <th style={{ minWidth: 200 }}>{t('orders.table.items')}</th>
                            <th>{t('orders.table.actions')}</th>
                        </tr>
                        </thead>
                        <tbody>
                        {filteredOrders.map(order => (
                            <tr key={order.orderId}>
                                <td>{order.orderId}</td>
                                <td>{new Date(order.orderDate).toLocaleString()}</td>
                                <td>{order.status}</td>
                                <td>{order.totalPrice}</td>
                                <td>{order.firstname}</td>
                                <td>{order.city}</td>
                                <td>{order.country}</td>
                                <td style={{ minWidth: 200 }}>
                                    {order.items?.length > 0 ? (
                                        <ul className="list-unstyled mb-0">
                                            {order.items.map((item, idx) => (
                                                <li key={idx}>{item.name} × {item.quantity}</li>
                                            ))}
                                        </ul>
                                    ) : t('orders.table.noItems')}
                                </td>
                                <td>
                                    <button
                                        className="btn btn-primary btn-sm"
                                        onClick={() => openModal(order)}
                                    >
                                        {t('actions.details')}
                                    </button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <p className="text-center">{t('orders.noOrders')}</p>
            )}

            <div className="d-flex justify-content-center align-items-center my-3">
                <button
                    className="btn btn-outline-primary me-2"
                    onClick={() => setPage(prev => Math.max(prev - 1, 0))}
                    disabled={page <= 0}
                >
                    ← {t('orders.pagination.previous')}
                </button>
                <span>{t('orders.pagination.page')} {page + 1} {t('orders.pagination.of')} {totalPages}</span>
                <button
                    className="btn btn-outline-primary ms-2"
                    onClick={() => setPage(prev => Math.min(prev + 1, totalPages - 1))}
                    disabled={page >= totalPages - 1}
                >
                    {t('orders.pagination.next')} →
                </button>
            </div>

            {/* Модалка */}
            {renderModal()}
        </div>
    );
};

export default GetOrder;